import React, { useEffect, useState } from "react";
import {
  Card,
  Row,
  Col,
  Typography,
  Button,
  Input,
  Table,
  Space,
  Tooltip,
  message,
  Popconfirm,
} from "antd";
import {
  PlusOutlined,
  FileExcelOutlined,
  SearchOutlined,
  ReloadOutlined,
  EditOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import { Link } from "react-router-dom";
import axiosInstance from "../../configs";
import dayjs from "dayjs";

const { Title, Text } = Typography;

const defaultPageSize = 20;

export default function BrowseItem() {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: defaultPageSize,
    total: 0,
  });

  const [searchName, setSearchName] = useState("");
  const [searchCode, setSearchCode] = useState("");
  const [deletingId, setDeletingId] = useState(null);

  const fetchItems = async (page = 1, pageSize = defaultPageSize) => {
    setLoading(true);
    try {
      const res = await axiosInstance.get("/adjustment", {
        params: {
          page,
          limit: pageSize,
          item_name: searchName || undefined,
          item_code: searchCode || undefined,
        },
      });

      const raw = res.data || {};
      const payload = raw.data || raw;
      const rows =
        payload.items ||
        payload.rows ||
        payload.data ||
        (Array.isArray(payload) ? payload : []);

      const total =
        payload.total ||
        payload.count ||
        payload.pagination?.total ||
        rows.length;

      setItems(rows);
      setPagination({
        current: page,
        pageSize,
        total: total || 0,
      });
    } catch (err) {
      console.error(err);
      message.error("Gagal memuat daftar adjustment");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems(1, defaultPageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = () => {
    fetchItems(1, pagination.pageSize);
  };

  const handleResetFilter = () => {
    setSearchName("");
    setSearchCode("");
    fetchItems(1, defaultPageSize);
  };

  const handleTableChange = (pager) => {
    fetchItems(pager.current, pager.pageSize);
  };

  const handleExportExcel = () => {
    message.info("Fitur export Excel belum dihubungkan dengan API.");
  };

  const handleDelete = async (record) => {
    setDeletingId(record.id_adjustment);
    try {
      await axiosInstance.delete(`/adjustment/${record.id_adjustment}`);
      message.success("Adjustment berhasil dihapus");
      fetchItems(pagination.current, pagination.pageSize);
    } catch (err) {
      console.error(err);
      message.error("Gagal menghapus adjustment");
    } finally {
      setDeletingId(null);
    }
  };

  const formatDate = (value) =>
    value ? dayjs(value).format("DD-MM-YY") : "-";

  const columns = [
    {
      title: "NO",
      dataIndex: "index",
      width: 50,
      align: "center",
      fixed: "left",
      render: (text, record, index) =>
        (pagination.current - 1) * pagination.pageSize + index + 1,
    },
    {
      title: "NO ADJUSTMENT",
      dataIndex: "adjustment_number",
      width: 130,
      ellipsis: true,
    },
    {
      title: "TANGGAL",
      dataIndex: "adjustment_date",
      width: 90,
      render: (value) => formatDate(value),
    },
    {
      title: "ALASAN",
      dataIndex: "reason",
      ellipsis: true,
      responsive: ["md"],
      render: (value) => value || "-",
    },
    {
      title: "AKSI",
      key: "action",
      width: 90,
      align: "center",
      fixed: "right",
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Edit">
            <Link to={`other/adjustment/update/${record.id_adjustment}`}>
              <Button
                size="small"
                type="primary"
                icon={<EditOutlined />}
              />
            </Link>
          </Tooltip>

          <Popconfirm
            title="Hapus Adjustment?"
            okText="Ya"
            cancelText="Batal"
            okButtonProps={{ danger: true, loading: deletingId === record.id_adjustment }}
            onConfirm={() => handleDelete(record)}
          >
            <Tooltip title="Hapus">
              <Button
                danger
                size="small"
                icon={<DeleteOutlined />}
                loading={deletingId === record.id_adjustment}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Card className="kt-portlet" bodyStyle={{ padding: "16px" }}>
      {/* HEADER */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} md={12}>
          <Title level={4} style={{ margin: 0 }}>Browse Adjustment</Title>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Kelola daftar adjustment inventory
          </Text>
        </Col>
        <Col xs={24} md={12}>
          <Space wrap style={{ width: "100%", justifyContent: "flex-end" }}>
            <Link to="/other/adjustment/add">
              <Button type="primary" icon={<PlusOutlined />}>
                Tambah
              </Button>
            </Link>
            <Button icon={<FileExcelOutlined />} onClick={handleExportExcel}>
              Excel
            </Button>
          </Space>
        </Col>
      </Row>

      {/* FILTER */}
      <Card bodyStyle={{ padding: "12px" }} style={{ marginBottom: 16, borderRadius: 8 }}>
        <Row gutter={[12, 12]}>
          <Col xs={24} sm={12} md={8}>
            <Text strong style={{ fontSize: 12 }}>Cari Nama Barang</Text>
            <Input
              allowClear
              prefix={<SearchOutlined />}
              placeholder="Cari..."
              size="small"
              value={searchName}
              onChange={(e) => setSearchName(e.target.value)}
              onPressEnter={handleSearch}
            />
          </Col>

          <Col xs={24} sm={12} md={8}>
            <Text strong style={{ fontSize: 12 }}>Cari Kode Barang</Text>
            <Input
              allowClear
              prefix={<SearchOutlined />}
              placeholder="Cari..."
              size="small"
              value={searchCode}
              onChange={(e) => setSearchCode(e.target.value)}
              onPressEnter={handleSearch}
            />
          </Col>

          <Col xs={24} md={8} style={{ display: "flex", alignItems: "flex-end", justifyContent: "flex-end" }}>
            <Space>
              <Button icon={<ReloadOutlined />} size="small" onClick={handleResetFilter}>
                Reset
              </Button>
              <Button type="primary" icon={<SearchOutlined />} size="small" onClick={handleSearch}>
                Cari
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      <Table
        rowKey="id_adjustment"
        dataSource={items}
        columns={columns}
        loading={loading}
        size="small"
        scroll={{ x: 500 }}
        pagination={{
          ...pagination,
          showSizeChanger: true,
          size: "small",
          showTotal: (total) => `Total ${total}`,
        }}
        onChange={handleTableChange}
      />
    </Card>
  );
}
